import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAllCohortUsersViewCreator } from '@app/view-creator/active-participants-view-creator';
import { DashboardViewCreator } from '@app/view-creator';
import { useChatContext } from 'stream-chat-react';
import { Channel as StreamChannel } from 'stream-chat';
import { NotificationService, CommunicationMode } from '@stores';
import logger from '@utils/logger';
import { useAppCookies } from '@app/providers/AppCookieProvider';

export const useDirectMessageController = () => {
  const { groupId, participantId } = useParams<{ groupId: string; participantId: string }>();
  const [channel, setChannel] = useState<StreamChannel | undefined>(undefined);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [groupName, setGroupName] = useState('Direct Message');
  const [participantRole, setParticipantRole] = useState<string | null>(null);
  const { data } = useAllCohortUsersViewCreator(Number(groupId));
  const allActiveCohortUsers = data?.allActiveCohortUsers || [];
  const dmRecipient = allActiveCohortUsers.filter((p) => p.CohortUser.userId === Number(participantId));
  const cookies = useAppCookies();
  const { client } = useChatContext();
  const navigate = useNavigate();

  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 2000;
  const retryCount = useRef(0);
  const clearRetryState = () => (retryCount.current = 0);

  useLayoutEffect(() => {
    const headerEl = document.querySelector('header');
    if (!headerEl) return;
    const update = () => setHeaderHeight(headerEl.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!groupId || !participantId || !cookies.profile.available || !client || !allActiveCohortUsers) return;
    setupChannel();
    loadDashboardView();
  }, [groupId, participantId, cookies.profile.id, client, allActiveCohortUsers]);

  const setupChannel = async () => {
    const channelId = `dm_cohort_${groupId}_${[cookies.profile.id, participantId].sort().join('_')}`;
    const channel = client.channel('messaging', channelId, {
      members: [cookies.profile.id.toString(), participantId],
      customType: `dm_cohort_${groupId}`,
    });
    await channel.watch();
    if (!channel || !channel.id) {
      if (retryCount.current < MAX_RETRIES) {
        console.warn('Failed to set up DM channel, retrying...');
        retryCount.current += 1;
        const delay = RETRY_DELAY_MS * retryCount.current;
        setTimeout(() => {
          setupChannel();
        }, delay);
      } else {
        logger.error('Failed to set up DM channel after retries');
        clearRetryState();
        navigate('/404', { replace: true });
        return;
      }
    }
    setChannel(channel);
    const selected = allActiveCohortUsers?.find((p) => p.CohortUser.userId === Number(participantId));
    setGroupName(`${selected?.name || selected?.email}`);
  };

  const loadDashboardView = async () => {
    try {
      const result = await new DashboardViewCreator().createViewWithArg({
        groupId: Number(groupId),
        userId: String(cookies.profile.id),
      });
      const selected = allActiveCohortUsers?.find((p) => p.CohortUser.userId === Number(participantId));
      if (selected && result.accountabilityPartners?.includes(selected?.id)) {
        setParticipantRole('Accountability Partner');
      } else if (selected?.CohortUser.role === 'Participant') {
        setParticipantRole('Hater');
      } else if (selected?.CohortUser.role === 'Coach') {
        setParticipantRole('Coach');
      } else if (selected?.CohortUser.role === 'Captain') {
        setParticipantRole('Captain');
      } else if (selected?.CohortUser.role === 'Admin') {
        setParticipantRole('Admin');
      } else {
        setParticipantRole(null);
      }
    } catch (error) {
      logger.error('Failed to load dashboard view', { error });
    }
  };

  const notifyParticipant = useCallback(async () => {
    await new NotificationService().sendNotification({
      groupId: Number(groupId),
      message: 'You have a new direct message! Click here to read it and respond: https://wheapp.com/',
      groupName,
      title: 'New We Hate Exercise Message',
      userId: cookies.profile.id,
      communicationMode: CommunicationMode.DIRECT_MESSAGE,
      recipientId: Number(participantId),
    });
  }, [groupId, groupName, cookies.profile.id, participantId]);

  return {
    channel,
    groupName,
    headerHeight,
    allActiveCohortUsers,
    dmRecipient,
    participantRole,
    notifyParticipant,
  };
};
